import React, { useEffect, useRef, useState } from "react";
import { Button, Snackbar } from "@mui/material";

// CRA regenerates asset-manifest.json with a fresh hashed main.js path on every
// build. We snapshot it at startup and re-check on focus/interval; if the path
// changed, a newer build is live and we invite a reload. This is what makes
// already-open installed PWAs pick up new deploys without a manual relaunch.
const MANIFEST_URL = "/asset-manifest.json";
const POLL_MS = 60 * 1000;

async function fetchMainHash() {
  try {
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return (data.files && data.files["main.js"]) || null;
  } catch (_) {
    return null;
  }
}

export default function UpdatePrompt() {
  const baseline = useRef(null);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const current = await fetchMainHash();
      if (cancelled || !current) return;
      if (baseline.current == null) {
        baseline.current = current; // first successful read = the running build
        return;
      }
      if (current !== baseline.current) setUpdateReady(true);
    };

    check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const timer = setInterval(check, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  if (!updateReady) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      message="A new version of Phieg CRM is available."
      sx={{
        // Sit above the mobile bottom nav + the home indicator.
        bottom: {
          xs: "calc(72px + var(--safe-area-bottom, 0px))",
          sm: 24,
        },
      }}
      action={
        <Button
          color="primary"
          size="small"
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      }
    />
  );
}
